/*
 * File: AssignedRule.tsx
 * Project: beaker
 * -----
 * Last Modified: 27 Apr 2022 11:11:11 am
 * -----
 * Copyright (c) 2022 Elegant Software Solutions All rights reserved.
 */

import { ArrowLeftOutlined, PlusCircleOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Card, Col, Collapse, Dropdown, List, Menu, message, Row } from 'antd';
import { BlockLoader } from 'components/atoms/BlockLoader';
import { FancyInput } from 'components/atoms/Inputs';
import { RolesCard } from 'components/atoms/RolesCard';
import { BusinessListItem } from 'components/molecules/BusinessListItem';
import { PrinterModal } from 'components/molecules/PrinterModal';
import { UserForm } from 'components/molecules/UserForm';
import { WarehouseModal } from 'components/molecules/WarehouseModal';
import { PrinterTable } from 'components/organisms/PrinterTable';
import { WarehouseTable } from 'components/organisms/WarehouseTable';
import { Stack } from 'components/UI';
import { AntButton } from 'components/UI/AntButton';
import { AntPageTitle } from 'components/UI/AntPageTitle';
import { FormikProvider, useFormik } from 'formik';
import { Business } from 'models/Business';
import { DivisionsModel } from 'models/DivisionsModel';
import { InlineStylesModel } from 'models/InlineStylesModel';
import { createUserPayload, EditUserPayload } from 'models/Users';
import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGetBusinessesQuery } from 'redux/services/abbyCadabby/abbyCadabbyApi';
import { useDeleteUserMutation, useGetRolesQuery, useGetUserQuery, useUpdateUserMutation } from 'redux/services/cookieMonster/cookieMonsterApi';
import { setCustomerSearch } from 'redux/slices/businessListSlice';
import { resetSelectedDivisions, setSelectedDivisions } from 'redux/slices/selectedDivisionSlice';
import { resetRoles, setRoles } from 'redux/slices/selectedRoles';
import { ReduxState } from 'redux/store';
import { useDebouncedCallback } from 'use-debounce';
import { invalidateUserRedisCache } from 'utils/setRedisCache';

const styles: InlineStylesModel = {
  //
};

export const EditUserPage: React.FC = () => {
  /* ******************** Hooks ******************** */
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [updateUser] = useUpdateUserMutation();
  const [deleteUser, { isLoading }] = useDeleteUserMutation();
  const { pathname } = useLocation();
  const userId = pathname.includes('/users/') && pathname.split('/')[2];
  const stringUserId = userId as string;
  const [visible, setVisible] = useState(false);
  const [visiblePrinter, setVisiblePrinter] = useState(false);

  const { data: rolesData, isLoading: isRolesDataLoading, isFetching: isRolesDataFetching } = useGetRolesQuery({ includePermissions: true });
  const { data: userData, isFetching: isUserFetching } = useGetUserQuery(stringUserId);
  const { data: businessData, isLoading: isBusinessLoading, isFetching: isBusinessFetching } = useGetBusinessesQuery();
  const { selectedRoles, unassignRoles, unassignDivisions } = useSelector((state: ReduxState) => state.selectedRoles);
  const { selectedDivisions } = useSelector((state: ReduxState) => state.selectedDivisions);
  const { customerSearch } = useSelector((state: ReduxState) => state.businessList);

  let applicationName = '';

  /* ******************** API Hooks ******************** */
  if (userData && !isUserFetching) {
    applicationName = userData.name;
  } else {
    applicationName = '';
  }

  const filteredData = useMemo(() => {
    let returnData: Business[] = [];

    // If business and divisions loaded
    if (businessData) {
      for (const business of businessData.data) {
        // Map the divisions
        let fullDivisions: DivisionsModel[] = [];

        for (const division of business.divisions) {
          fullDivisions = [...fullDivisions, ...[division]];
        }

        // Add business filtered for customer search
        returnData = [...returnData, { ...business, divisions: fullDivisions }].map((business) => ({
          ...business,
          divisions: business.divisions.filter((division) => !customerSearch || division.name.toLowerCase().includes(customerSearch) || division.erpId.toLowerCase().includes(customerSearch))
        }));
      }
    }

    // Only return the search values that contain divisions
    const filteredReturnData = customerSearch?.length ? returnData.filter((business) => business.divisions.length) : returnData;

    return filteredReturnData;
  }, [businessData, customerSearch]);

  const roleIdArray = userData?.roles.map((role) => role.id);
  const divisionIdArrayFlat = userData?.app_metadata.businesses.map((business) => business.divisions.map((division) => division.id)).flat();

  const formik = useFormik<EditUserPayload>({
    validationSchema: createUserPayload,
    enableReinitialize: true,
    initialValues: {
      isMdsiEmployee: userData ? userData?.user_metadata.is_mdsi_employee : undefined,
      employeeERPId: userData ? userData.user_metadata.employee_erp_id : '',
      email: userData ? userData.email : '',
      givenName: userData ? userData.given_name : '',
      familyName: userData ? userData.family_name : '',
      phoneNumber: userData ? userData.user_metadata.phone_number : '',
      divisionIdsToAssign: selectedDivisions,
      divisionIdsToUnassign: unassignDivisions,
      roleIdsToAssign: selectedRoles,
      roleIdsToUnassign: unassignRoles,
      authorizedWarehouses: userData ? userData.user_metadata.authorized_warehouses : [],
      assignedPrinters: userData ? userData.user_metadata.assigned_printers : []
    },
    onSubmit: async (values) => {
      if (!userData) {
        message.error("User doesn't exist");

        return;
      }
      try {
        const updateData = await updateUser({
          userId: stringUserId,
          payload: { ...values, ...{ employeeERPId: values.employeeERPId?.includes('-') ? values.employeeERPId?.split('-')[1].trimStart() : values.employeeERPId } }
        }).unwrap();

        message.success(`User ${updateData.name} was successfully updated!`);

        await invalidateUserRedisCache(userData.user_id);
      } catch (error) {
        console.log(error);
        message.error((error as { data: string }).data ?? 'User failed to be updated!');
      }
    }
  });

  useEffect(() => {
    if (userData && !selectedRoles.length && roleIdArray) {
      dispatch(setRoles(roleIdArray));
    }
    if (userData && !selectedDivisions.length && divisionIdArrayFlat) {
      dispatch(setSelectedDivisions(divisionIdArrayFlat));
    }
  }, [userData]);

  useEffect(() => {
    return (): void => {
      dispatch(resetRoles());
      dispatch(resetSelectedDivisions());
    };
  }, []);

  /* ******************** Functions ******************** */

  const handleCustomerSearchChange = useDebouncedCallback((event: ChangeEvent<HTMLInputElement>) => {
    dispatch(setCustomerSearch(event.target.value));
  }, 500);

  const handleClose = (): void => {
    navigate('/');
  };

  const handleSave = async (close?: boolean): Promise<void> => {
    await formik.submitForm();
    if (close) {
      handleClose();
    }
  };

  const handleDelete = async (close?: boolean): Promise<void> => {
    if (!updateUser) {
      message.error("User doesn't Exist");

      return;
    }
    try {
      await deleteUser({ userId: stringUserId, params: { wouldYouLikeToPlayAGame: false } });
      message.success('User successfuly deleted');
      if (close) {
        handleClose();
      }
    } catch (error) {
      message.error((error as { data: string }).data ?? 'User failed to be Deleted!');
    }
  };

  if (isUserFetching) {
    return (
      <Row justify="center" align="middle">
        <Col span={24}>
          <BlockLoader direction="loader loader--slideUp" />
        </Col>
      </Row>
    );
  }
  const menuJSX = (
    <Menu disabledOverflow={true}>
      <Menu.Item key="1" onClick={(): Promise<void> => handleSave(false)}>
        Save
      </Menu.Item>
    </Menu>
  );

  return (
    <FormikProvider value={formik}>
      <Stack flexDirection="column">
        <Col style={{ padding: 10, background: 'rgb(206, 213, 242)' }}>
          <Stack justifyContent="space-between">
            <AntPageTitle text={`Edit User: ${applicationName}`} />
            <Stack>
              <Button onClick={handleClose} icon={<ArrowLeftOutlined />}>
                Back
              </Button>

              <Dropdown.Button overlay={menuJSX} onClick={(): Promise<void> => handleSave(true)} loading={formik.isSubmitting} type="primary" icon={<SaveOutlined />}>
                Save and Exit
              </Dropdown.Button>
              <AntButton onClick={(): Promise<void> => handleDelete(true)} loading={isLoading} danger type="ghost" icon={<SaveOutlined />} style={styles.saveButton}>
                Delete
              </AntButton>
            </Stack>
          </Stack>
        </Col>
        <Card>
          <UserForm />
        </Card>
        <Collapse style={{ background: 'rgb(206, 213, 242)' }}>
          <Collapse.Panel showArrow={false} header={<AntPageTitle text="Edit/Assign Roles" />} key={1}>
            <Row gutter={[32, 16]}>
              <Col span={24}>
                <List
                  loading={{
                    indicator: <BlockLoader direction="loader loader--slideUp" />,
                    size: 'large',
                    spinning: isRolesDataFetching || isRolesDataLoading
                  }}
                  dataSource={rolesData?.data}
                  renderItem={(item) => <RolesCard userData={userData} roles={item} />}
                />
              </Col>
            </Row>
          </Collapse.Panel>
        </Collapse>
        <Collapse style={{ background: 'rgb(206, 213, 242)' }}>
          <Collapse.Panel showArrow={false} header={<AntPageTitle text="Edit/Assign Customers" />} key={1}>
            <Row gutter={[32, 16]}>
              <Col span={6} style={{ marginBottom: 15 }}>
                <FancyInput placeholder="Search customers" onChange={handleCustomerSearchChange} />
              </Col>
              <Col span={24}>
                <List
                  loading={{
                    indicator: <BlockLoader direction="loader loader--slideUp" />,
                    size: 'large',
                    spinning: isBusinessLoading || isBusinessFetching
                  }}
                  dataSource={filteredData}
                  renderItem={(item): JSX.Element => <BusinessListItem userData={userData} business={item} />}
                  rowKey={(item): string => item.id}
                />
              </Col>
            </Row>
          </Collapse.Panel>
        </Collapse>
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Collapse style={{ background: 'rgb(206, 213, 242)' }}>
              <Collapse.Panel
                showArrow={false}
                header={<AntPageTitle text="Edit/Assign Warehouses" />}
                key={1}
                extra={
                  <Button
                    onClick={(ev) => {
                      ev.stopPropagation();
                      setVisible(true);
                    }}
                    icon={<PlusCircleOutlined />}>
                    Assign Warehouse
                  </Button>
                }>
                <Row gutter={[32, 16]}>
                  <Col span={24}>
                    <WarehouseTable />
                  </Col>
                </Row>
              </Collapse.Panel>
            </Collapse>
          </Col>
          <Col span={12}>
            <Collapse style={{ background: 'rgb(206, 213, 242)' }}>
              <Collapse.Panel showArrow={false} header={<AntPageTitle text="Edit/Assign Authorized Printers" />} key={1} extra={<PrinterModal />}>
                <Row gutter={[32, 16]}>
                  <Col span={24}>
                    <PrinterTable />
                  </Col>
                </Row>
              </Collapse.Panel>
            </Collapse>
          </Col>
        </Row>
        <WarehouseModal setVisibility={setVisible} visible={visible} onOk={() => console.log('ok')} onCancel={() => setVisible(false)} warehouse={'lol'} />
      </Stack>
    </FormikProvider>
  );
};
